// Iter 90o — First-login walkthrough for new admins.
//
// Zero external dependencies: renders a fixed-position spotlight around a
// target element (by data-testid or CSS selector), plus a tooltip card
// with Prev / Next / Finish. Persists "seen" state per user so it never
// re-shows once dismissed.
//
// Steps are defined in DEFAULT_STEPS below. The tour is auto-started on
// first login for any staff role by <OnboardingHost> which lives in the
// global Layout. Users can retrigger it from the account menu.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const CTX = createContext(null);
export const useOnboarding = () => useContext(CTX) || { start: () => {}, running: false };

const DEFAULT_STEPS = [
  {
    id: "welcome",
    title: "Welcome to Conduit",
    body: "Quick 60-second tour of the four places you'll live most of the time. You can replay this any time from your avatar menu.",
    route: "/dashboard",
    target: null,          // centre-of-screen for the intro
  },
  {
    id: "dashboard",
    title: "Dashboard — your launchpad",
    body: "Every app you have access to lives here. Applications above the fold, Internal Only tools below, and anything in Roadmap is IP-locked. Drag tiles to rearrange them.",
    route: "/dashboard",
    target: '[data-testid="dashboard-section-shared"]',
    perm: "dashboard.view",
  },
  {
    id: "job-board",
    title: "Job Board — hiring pipeline",
    body: "Track every candidate, their AI score, and their journey through departments. Bulk-park, bulk-transfer, and PDF-export top picks all live here.",
    route: "/jobs",
    target: 'main',
    perm: "job_board.view",
  },
  {
    id: "did-master",
    title: "DID Master — the number of truth",
    body: "37,000+ DIDs, live sync from PBX, alias-based partner assignment. Upload a CSV to refresh; the Columns/CSV/PDF toolbar in the top right works the same on every list page.",
    route: "/inventory-hub/did-master",
    target: '[data-testid="did-columns-btn"]',
    // The route itself is guarded by `inventory.view` — using `did.view` here
    // would send a user without inventory access to a redirect (Iter 115f).
    perm: "inventory.view",
  },
  {
    id: "kill-switch",
    title: "Kill Switch — safety valve",
    body: "The banner up top pauses outbound email globally. QA allowlist lets specific addresses or entire domains bypass it. Toggle it any time from the Dashboard.",
    route: "/dashboard",
    target: '[data-testid="kill-switch-banner"], [data-testid^="email-pause"]',
    perm: "settings_notifications.edit",
  },
  {
    id: "done",
    title: "You're set",
    body: "That's the map. Everything else is discoverable from the sidebar. Have fun!",
    route: null,
    target: null,
  },
];

function Spotlight({ rect, padding = 8 }) {
  if (!rect) return null;
  const style = {
    top:    rect.top - padding,
    left:   rect.left - padding,
    width:  rect.width + padding * 2,
    height: rect.height + padding * 2,
  };
  return (
    <div className="pointer-events-none fixed inset-0 z-[9998]">
      <div className="absolute inset-0 bg-slate-950/55" style={{
        clipPath: `polygon(0 0, 100% 0, 100% 100%, 0 100%, 0 0,
          ${style.left}px ${style.top}px,
          ${style.left}px ${style.top + style.height}px,
          ${style.left + style.width}px ${style.top + style.height}px,
          ${style.left + style.width}px ${style.top}px,
          ${style.left}px ${style.top}px)`,
      }} />
      <div className="absolute rounded-lg ring-2 ring-primary shadow-[0_0_0_9999px_rgba(0,0,0,0)]"
           style={style} />
    </div>
  );
}

function TooltipCard({ step, idx, total, rect, onPrev, onNext, onSkip }) {
  const style = useMemo(() => {
    if (!rect) {
      return { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
    }
    // Prefer showing below the target; if that would overflow, show above.
    const below = rect.bottom + 16;
    const wouldOverflow = below + 220 > window.innerHeight;
    const top = wouldOverflow ? Math.max(16, rect.top - 220 - 16) : below;
    const left = Math.min(
      Math.max(16, rect.left + rect.width / 2 - 200),
      window.innerWidth - 400 - 16,
    );
    return { top, left };
  }, [rect]);
  return (
    <div className="fixed z-[9999] w-[400px] max-w-[calc(100vw-32px)] rounded-xl border border-slate-200 bg-white p-4 shadow-2xl"
         style={style}
         data-testid={`onboarding-step-${step.id}`}
         role="dialog" aria-labelledby="onboarding-title">
      <div className="mb-1.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-primary">
        <Sparkles className="h-3.5 w-3.5" /> Step {idx + 1} of {total}
      </div>
      <h3 id="onboarding-title"
          className="mb-1 text-base font-semibold text-slate-900">{step.title}</h3>
      <p className="mb-3 text-sm text-slate-600 leading-relaxed">{step.body}</p>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="ghost" onClick={onSkip}
                data-testid="onboarding-skip"
                className="text-slate-500 hover:text-slate-800">
          Skip tour
        </Button>
        <div className="ml-auto flex items-center gap-1.5">
          <Button size="sm" variant="outline" onClick={onPrev} disabled={idx === 0}
                  data-testid="onboarding-prev">
            <ChevronLeft className="h-4 w-4" /> Back
          </Button>
          <Button size="sm" onClick={onNext} data-testid="onboarding-next"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground">
            {idx === total - 1 ? "Finish" : (<>Next <ChevronRight className="h-4 w-4" /></>)}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function OnboardingHost({ user, children }) {
  const [running, setRunning] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const timer = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { has } = useAuth() || {};

  // Iter 115e — Only tour the places this user can actually reach. A step
  // whose `perm` isn't held is dropped, so e.g. a fresh TechOps user without
  // `job_board.view` never gets walked into the Job Board.
  const steps = useMemo(
    () => DEFAULT_STEPS.filter((s) => !s.perm || (has ? has(s.perm) : false)),
    [has],
  );
  const step = steps[idx];

  const seenKey = user?.user_id ? `onboarding_seen_${user.user_id}` : null;

  // Auto-start once per user (staff only — partners/customers already
  // know the layout from a different orientation).
  useEffect(() => {
    if (!user || !seenKey) return;
    const seen = localStorage.getItem(seenKey);
    if (seen) return;
    const eligible = user.role === "super_admin"
      || user.base_role === "staff";
    if (!eligible) return;
    // Iter 91n — never launch the onboarding tour while the forced
    // password-change modal is up; it would render on top of the
    // temporary-password fields and block the input.
    if (user.must_change_password) return;
    // Iter 265 — the celebration gate owns the screen until it's satisfied.
    if (user.needs_celebration_dates && !user.masqueraded_by) return;
    // Never hijack a deep link. Step 1 lives on /dashboard, so auto-starting
    // here would navigate the user off the article, report or settings page
    // they actually asked for. Wait until they land on the dashboard; the tour
    // is also replayable any time from the avatar menu.
    if (location.pathname !== "/dashboard" && location.pathname !== "/") return;
    const t = setTimeout(() => setRunning(true), 1200);
    return () => clearTimeout(t);
  }, [user, seenKey, location.pathname]);

  const measure = useCallback(() => {
    if (!step?.target) { setRect(null); return; }
    const el = document.querySelector(step.target);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height,
              bottom: r.bottom });
  }, [step]);

  // On step change: navigate if needed, then measure (retrying a few times
  // in case the target element mounts asynchronously).
  useEffect(() => {
    if (!running) return;
    if (step?.route) navigate(step.route);
    setRect(null);
    let attempts = 0;
    if (timer.current) clearInterval(timer.current);
    timer.current = setInterval(() => {
      measure();
      attempts += 1;
      if (attempts > 20) {
        clearInterval(timer.current); timer.current = null;
        // Iter 115f — safety net: if the target never mounts (the route
        // redirected because the viewer lacks access, or the element was
        // renamed), skip ahead instead of stranding the tour on a blank step.
        if (step?.target && !document.querySelector(step.target)) {
          setIdx((i) => (i < steps.length - 1 ? i + 1 : i));
        }
      }
    }, 150);
    return () => { if (timer.current) clearInterval(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, running]);

  useEffect(() => {
    if (!running) return;
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onResize, true);
    };
  }, [running, measure]);

  const finish = useCallback(() => {
    setRunning(false);
    setIdx(0);
    if (seenKey) localStorage.setItem(seenKey, "1");
  }, [seenKey]);

  const api = useMemo(() => ({
    running,
    start: () => { setIdx(0); setRunning(true); },
    stop:  finish,
  }), [running, finish]);

  return (
    <CTX.Provider value={api}>
      {children}
      {running && step && (
        <>
          <Spotlight rect={rect} />
          <TooltipCard step={step} idx={idx} total={steps.length}
                       rect={rect}
                       onPrev={() => setIdx((i) => Math.max(0, i - 1))}
                       onNext={() => {
                         if (idx === steps.length - 1) finish();
                         else setIdx((i) => i + 1);
                       }}
                       onSkip={finish} />
          <button data-testid="onboarding-close"
                  onClick={finish}
                  aria-label="Close tour"
                  className="fixed right-6 top-6 z-[9999] flex h-9 w-9 items-center justify-center rounded-full bg-white text-slate-600 shadow-lg hover:bg-slate-100">
            <X className="h-4 w-4" />
          </button>
        </>
      )}
    </CTX.Provider>
  );
}
